/**
 * api.js — single fetch wrapper that all client→PHP calls should go through.
 *
 * Why centralize: PHP endpoints used to trust a `uid` value sent in the
 * request body. With firebase_auth.php now verifying Firebase ID tokens
 * server-side, every API call needs an `Authorization: Bearer <jwt>`
 * header. Keeping that in one place avoids "forgot to attach the token"
 * regressions and gives us a single retry-on-401 path.
 *
 * Behavior:
 *   - Attaches the current user's Firebase ID token if one exists.
 *   - JSON-encodes a `body` argument unless caller passes FormData.
 *   - On 401 with a "token expired"-ish status, force-refreshes the token
 *     and retries the request exactly once. This handles the case where
 *     the cached token expired between cache lookup and request.
 *   - Throws an Error (with .status, .body) on non-2xx responses.
 *   - Returns parsed JSON on 2xx (or `null` for empty bodies).
 *
 * Migration callout: many existing fetch / axios sites in the codebase
 * still hand-roll their auth flow. They keep working (token is optional
 * for read endpoints via optional_uid()) but should be migrated to
 * apiCall() opportunistically. The endpoints touched by the user-profile
 * PR have been migrated; the long tail is tracked in TODOS.md under
 * "Phase 2 auth hardening".
 */

import config from './config';
import { getIdToken } from './firebase';

class ApiError extends Error {
    constructor(message, status, body) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
        this.body = body;
    }
}

const isFormData = (v) => typeof FormData !== 'undefined' && v instanceof FormData;

async function buildHeaders(extra, body, forceRefreshToken) {
    const headers = { Accept: 'application/json', ...(extra || {}) };
    if (body !== undefined && !isFormData(body) && !headers['Content-Type']) {
        headers['Content-Type'] = 'application/json';
    }
    try {
        const token = await getIdToken(forceRefreshToken);
        if (token && !headers.Authorization) {
            headers.Authorization = `Bearer ${token}`;
        }
    } catch (_err) {
        // Token fetch can fail (e.g. user signed out mid-call). Endpoints
        // that require auth will 401; ones that don't will still work.
    }
    return headers;
}

async function doFetch(url, opts, forceRefreshToken) {
    const { method = 'GET', body, headers: extraHeaders, query, signal } = opts;
    const finalUrl = query
        ? `${url}${url.includes('?') ? '&' : '?'}${new URLSearchParams(query).toString()}`
        : url;

    const headers = await buildHeaders(extraHeaders, body, forceRefreshToken);
    const init = { method, headers, signal };

    if (body !== undefined) {
        init.body = isFormData(body) || typeof body === 'string' ? body : JSON.stringify(body);
    }

    return fetch(finalUrl, init);
}

/**
 * Make an authenticated API call to a PHP endpoint.
 *
 * @param {string} endpoint  Endpoint filename (e.g. 'getUserProfile.php') OR
 *                           a full URL (passed through unchanged).
 * @param {object} [opts]
 * @param {string} [opts.method='GET']
 * @param {object|FormData|string} [opts.body]
 * @param {object} [opts.query]    Appended as query string for GETs.
 * @param {object} [opts.headers]  Extra request headers.
 * @param {AbortSignal} [opts.signal]
 * @returns {Promise<any>}  Parsed JSON response.
 * @throws {ApiError}
 */
export async function apiCall(endpoint, opts = {}) {
    const url = /^https?:\/\//.test(endpoint) ? endpoint : config.getApiUrl(endpoint);

    let res = await doFetch(url, opts, false);

    // 401 → token may be stale. Force-refresh and retry exactly once.
    // We only retry for 401; other 4xx/5xx are returned to the caller as-is
    // because retrying them would mask real bugs.
    if (res.status === 401) {
        res = await doFetch(url, opts, true);
    }

    let parsed = null;
    const text = await res.text();
    if (text) {
        try {
            parsed = JSON.parse(text);
        } catch (_err) {
            parsed = { _raw: text };
        }
    }

    if (!res.ok) {
        const message =
            (parsed && (parsed.error || parsed.message)) ||
            `Request failed: ${res.status} ${res.statusText}`;
        throw new ApiError(message, res.status, parsed);
    }
    return parsed;
}

export { ApiError };
